import React, {Suspense, useState} from 'react';
import {Spinner} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import ErrorBoundary from '../components/ErrorBoundary';
import {
    Invoice,
    InvoicelessDocumentsInnerProps,
    TableInvoiceComponentProps,
} from '../models/table-models';
import {ErrorComponent} from '../components/ErrorComponent';
import {useAsyncResource} from 'use-async-resource';
import {useGetAllDocuments, useGetUsers} from '../utils/users-hooks';
import {InvoiceView} from './InvoiceView';
import dateFormat from "dateformat";
import {date} from "../utils/Enums/Common";
import {
    allDocumentsTableHeaders,
} from "../utils/Enums/TableHeaders";
import {CustomTable} from "../components/CustomTable";
import { getCurrencySymbol, getLetter, withPaidPart } from "../utils/helper-functions";
import {Role} from "../utils/Enums/Roles";
import {PaginationComponent} from "../components/Pagination";
import {NoDataComponent} from "../components/NoDataComponent";
import {InvoicelessDocumentView} from "../models/dashboard-models";
import {ExportDocuments} from "../exports/ExportDocuments";
import {Turnover} from "../models/user-models";

export const AllDocuments = ({filter, pagination, refetchEntitiesHook}: TableInvoiceComponentProps & {
    refetchEntitiesHook: () => Promise<void>,
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'common'});

    const [getAllDocuments, fetchAllDocuments] = useAsyncResource(useGetAllDocuments, pagination, filter);
    const allDocuments = getAllDocuments();

    const [getAllUsers] = useAsyncResource(useGetUsers, {}, 'ADMIN,DISTRIBUTOR,INSTALLER,COORDINATOR,FINANCIAL');
    const {items: users} = getAllUsers();
    const [selectedItem, setSelectedItem] = useState<InvoicelessDocumentView>();

    return (
        <>
            <InvoiceView
                title={t('Details for')}
                item={selectedItem?.invoicings ? selectedItem.invoicings[0] : undefined}
                // Двата реда на сторнирания документ носят едно и също invoicings[0], а сумата на самия
                // ред е празна за продажбите, тоест -total дава -0 и проверката по знак не се задейства.
                // Типът на реда е единственото, което наистина ги различава.
                isCreditNote={selectedItem?.documentType === 'CREDIT_NOTE' || selectedItem?.documentType === 'STORNO'}
                turnover={selectedItem ? {
                    cash: selectedItem.documentType === 'STORNO' || selectedItem.documentType === 'CREDIT_NOTE' ?
                        -selectedItem.total
                        :
                        selectedItem.total
                } as Turnover : undefined }
                setSelectedItem={() => setSelectedItem(undefined)}
                refetchEntitiesHook={() => {
                    refetchEntitiesHook();
                }}
            />
            <ErrorBoundary fallback={<ErrorComponent errorText={t('invoices')}/>}>
                <Suspense fallback={<Spinner animation='border'/>}>
                    <div
                        className='mb-3 p-3 d-flex justify-content-around align-items-center flex-wrap border-primary w-100'>
                        <div className='p-3 d-flex flex-column align-items-center'>
                            <Suspense fallback={<Spinner animation='border'/>}>
                                <ExportDocuments pagination={pagination} turnoversFilter={filter}
                                                 fileName={t('documents') + "-" + dateFormat(new Date(), date)}
                                                 wscols={allDocumentsTableHeaders} users={users}/>
                            </Suspense>
                        </div>
                    </div>

                    <DocumentsInner
                        pagination={pagination}
                        items={allDocuments.items ?? []}
                        users={users}
                        pager={allDocuments.pager}
                        setItem={setSelectedItem}
                        {...{refetchEntitiesHook}}
                    />
                </Suspense>
            </ErrorBoundary>
        </>
    );
};

const DocumentsInner = ({
                            items,
                            users,
                            pagination,
                            refetchEntitiesHook,
                            pager,
                            setItem,
                        }: InvoicelessDocumentsInnerProps) => {
    const {t} = useTranslation('translation', {keyPrefix: 'invoice'});
    const {t: commonT} = useTranslation('translation', {keyPrefix: 'common'});

    const transformedItems = items.map(item => ({
        ...item,
        cash: Number((item.total / 100).toFixed(2)),
    }));

    const getTotalPriceFromInvoicings = (invoicings: Invoice[]) => (invoicings.map(({total}) => total).reduce((a, b) => a + b, 0) / 100).toFixed(2);
    const getCurrencySymbolFromInvoicings = (invoicings: Invoice[]) => invoicings.length > 0 ? getCurrencySymbol(invoicings[0].currency) : '€';

    // Сумите на документа са в стотинки; платеното е сборът по редовете му.
    const totalInCents = (invoicings: Invoice[]) => invoicings.reduce((sum, row) => sum + row.total, 0);
    const paidInCents = (invoicings: Invoice[]) => invoicings.reduce((sum, row) => sum + (row.paidAmount ?? 0), 0);

    return (
        <>
            {items.length > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: allDocumentsTableHeaders,
                            items: transformedItems.map(({
                                                             documentType,
                                                             documentNumber,
                                                             cash,
                                                             paymentType,
                                                             invoiceDate,
                                                             invoicings,
                                                             responsibleUserIds,
                                                             type
                                                         }) => [
                                getLetter(cash, documentType, type),
                                documentNumber,
                                (responsibleUserIds ?? []).map(id => users.find(user => user.id === id)?.username ?? String(id)).join(', ') || '—',
                                dateFormat(invoiceDate, date),
                                invoicings && invoicings.length>0
                                    ? documentType === 'STORNO' || documentType === 'CREDIT_NOTE'
                                        ? Number(-getTotalPriceFromInvoicings(invoicings)) + getCurrencySymbolFromInvoicings(invoicings)
                                        : withPaidPart(
                                            Number(getTotalPriceFromInvoicings(invoicings)) + getCurrencySymbolFromInvoicings(invoicings),
                                            totalInCents(invoicings), paidInCents(invoicings), invoicings[0]?.currency,
                                            paymentType?.toUpperCase() === 'BANK', t('paid'))
                                    : documentType === 'STORNO' || documentType === 'CREDIT_NOTE'
                                        ? - cash
                                        : cash,
                                t(paymentType.toLowerCase())
                            ]),
                        }}
                        viewComponentObject={(document) => {
                            // Документният номер не стига за разпознаване на реда: продажбите изобщо нямат
                            // такъв, защото не минават през фискално устройство, а сторнираният документ
                            // дава два реда - самият документ и кредитното известие - с един и същи номер.
                            // Търсенето само по него връщаше първия съвпадащ ред, тоест фактурата вместо
                            // известието, и прегледът отваряше нея.
                            const item = items.find((value) =>
                                value.id === document.id && value.documentType === document.documentType);
                            if (setItem) {
                                setItem(item);
                            }
                        }}
                        accessEditRoles={[Role.FINANCIAL]}
                        accessActionRoles={[Role.ADMIN]}
                        tableData={transformedItems}
                        {...{refetchEntitiesHook}}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination}/>
                </>
            ) : (
                <NoDataComponent noDataText={commonT('payments')}></NoDataComponent>
            )}
        </>
    );
};
